import type { ReactNode } from "react";
import { YStack } from "tamagui";

import {
  Button,
  Card,
  Header,
  Heading,
  Li,
  Paragraph,
  Text,
  Ul,
} from "@/components";
import type { VisitOption } from "./visitOptions";

type VisitOptionCardProps = {
  option: VisitOption;
  priceLabel: ReactNode;
  loading: boolean;
  disabled?: boolean;
  selectDisabled?: boolean;
  onPress: () => void;
};

export default function VisitOptionCard({
  option,
  priceLabel,
  loading,
  disabled,
  selectDisabled,
  onPress,
}: VisitOptionCardProps) {
  return (
    <Card
      borderRadius="$150"
      flex={1}
      padding="$250"
      gap="$200"
      boxShadow="0 4px 32px $shadow"
      opacity={disabled ? 0.5 : 1}
    >
      <Header
        alignItems="flex-start"
        justifyContent="space-between"
        gap="$150"
        flexDirection="row"
      >
        <YStack>
          <Text size="$xs" medium>
            {option.titleModifier}
          </Text>
          <Heading size="$lg">{option.title}</Heading>
        </YStack>
        {!disabled && (
          <Text
            size="$2xs"
            bold
            backgroundColor="$fillClinician"
            paddingHorizontal="$125"
            paddingVertical="$100"
            borderRadius="$1000"
          >
            {priceLabel}
          </Text>
        )}
      </Header>

      <Paragraph size="$sm" color="$textSecondary">
        {option.description}
      </Paragraph>

      <Ul gap="$200" flex={1}>
        {option.details.map((detail, index) => (
          <Li
            key={`${option.visitType}-${index}`}
            marker={detail.icon}
            alignItems="center"
          >
            {detail.description}
          </Li>
        ))}
      </Ul>

      <Button
        dataTrackingId={`product-options-${option.visitType}-button`}
        mode="primary"
        loading={loading}
        // `loading` alone only sets aria-disabled (advisory); OR it into `disabled` so a
        // loading/submitting button is truly unclickable and can't be double-pressed.
        // `selectDisabled` blocks selection (e.g. patient responsibility still loading) while keeping the label.
        disabled={disabled || loading || selectDisabled}
        onPress={onPress}
      >
        {option.buttonLabel}
      </Button>
    </Card>
  );
}
